import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  CheckCircle,
  Building2,
  AlertTriangle,
  CloudUpload,
  ShieldCheck,
  Lock,
  FileCheck,
  Eye,
  Download,
  Home,
  TrendingUp,
  BarChart3,
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_tenure-reporting-hub/artifacts/fhf5fsd8_CityWorth%20Logo.png";

export default function PublicUploadPage() {
  const { token } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);

  useEffect(() => {
    loadCompany();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCompany = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/public/upload/${token}`);
      setCompany(response.data);
    } catch {
      setError('This upload link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/api/public/upload/${token}/file`, formData);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['Employee ID','First Name','Last Name','Start Date','Address','Address 2','City','State','Zip Code','Salary','Date of Birth','Email','Phone'];
    const sampleRow = ['EMP001','Jane','Smith','2023-01-15','123 Main St','Apt 4B','Miami','FL','33101','65000','1990-05-20','jane.smith@email.com','555-123-4567'];
    const csv = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'census_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-[#E31837] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg max-w-md w-full p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-8 h-8 text-[#E31837]" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Invalid Link</h2>
          <p className="text-slate-500">{error}</p>
          <p className="text-slate-400 text-sm mt-4">Please contact your CityWorth representative for a valid upload link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="CityWorth" className="h-9" />
          <div className="flex items-center gap-2 bg-slate-50 rounded-full px-4 py-1.5 border border-slate-100">
            <Building2 className="w-4 h-4 text-[#E31837]" />
            <span className="text-sm font-semibold text-slate-700">{company?.name}</span>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10">

        {!done ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {/* Retention Analysis Banner */}
            <div className="bg-slate-900 rounded-2xl p-8 mb-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#E31837]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
              <div className="relative z-10">
                <h1 className="text-2xl font-bold mb-3">Employee Retention Analysis</h1>
                <p className="text-slate-300 text-sm leading-relaxed max-w-2xl mb-6">
                  Upload your employee census data and we'll provide actionable insights to help optimize your benefits strategy and reduce turnover.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#E31837]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Home className="w-4 h-4 text-[#E31837]" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Homeowner Verification</p>
                      <p className="text-xs text-slate-400 mt-0.5">Identify homeownership rates across your workforce</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <TrendingUp className="w-4 h-4 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Tenure Analysis</p>
                      <p className="text-xs text-slate-400 mt-0.5">Track employee tenure trends and retention patterns</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <BarChart3 className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Cost Savings</p>
                      <p className="text-xs text-slate-400 mt-0.5">Data-driven insights to reduce turnover costs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Area */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
              <div className="px-8 pt-8 pb-4">
                <h2 className="text-xl font-bold text-slate-900">Upload Census File</h2>
                <p className="text-slate-500 mt-1 text-sm">Upload your employee census data as a CSV or Excel file. We'll take it from here.</p>
              </div>
              <div className="px-8 pb-8">
                <div
                  {...getRootProps()}
                  data-testid="dropzone-area"
                  className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-200
                    ${isDragActive
                      ? 'border-[#E31837] bg-red-50/50 scale-[1.01]'
                      : file
                        ? 'border-green-400 bg-green-50/30'
                        : 'border-slate-300 hover:border-[#E31837]/50 hover:bg-slate-50'
                    }`}
                >
                  <input {...getInputProps()} />
                  {file ? (
                    <>
                      <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <FileCheck className="w-7 h-7 text-green-600" />
                      </div>
                      <p className="text-lg font-semibold text-slate-800">{file.name}</p>
                      <p className="text-sm text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB — Click or drag to replace</p>
                    </>
                  ) : isDragActive ? (
                    <>
                      <div className="w-14 h-14 rounded-2xl bg-[#E31837]/10 flex items-center justify-center mx-auto mb-4">
                        <CloudUpload className="w-7 h-7 text-[#E31837]" />
                      </div>
                      <p className="text-lg font-semibold text-[#E31837]">Drop the file here...</p>
                    </>
                  ) : (
                    <>
                      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                        <CloudUpload className="w-7 h-7 text-slate-400" />
                      </div>
                      <p className="text-lg font-semibold text-slate-800 mb-1">Drag & drop your census file here</p>
                      <p className="text-sm text-slate-500">or <span className="text-[#E31837] font-medium">click to browse</span></p>
                      <p className="text-xs text-slate-400 mt-3">Supports CSV, XLS, XLSX</p>
                    </>
                  )}
                </div>

                {file && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5">
                    <Button
                      onClick={handleUpload}
                      disabled={uploading}
                      data-testid="submit-upload-button"
                      className="w-full text-white h-12 text-sm font-medium bg-[#E31837] hover:bg-[#c41530]"
                    >
                      {uploading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          Uploading securely...
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Upload Securely
                        </>
                      )}
                    </Button>
                  </motion.div>
                )}

                <div className="mt-6 flex items-center justify-between">
                  <p className="text-xs text-slate-400">Need a template?</p>
                  <button
                    onClick={downloadTemplate}
                    data-testid="download-template-button"
                    className="flex items-center gap-1.5 text-xs font-medium text-[#E31837] hover:text-[#c41530] transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download Template
                  </button>
                </div>
              </div>
            </div>

            {/* Secure Upload Info - Bottom */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Secure Census Data Upload</h3>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    This is a one-way secure upload portal created exclusively for <strong className="text-slate-700">{company?.name}</strong>. 
                    Your employee census data is transmitted over an encrypted connection and handled with strict confidentiality. 
                    Only authorized CityWorth administrators can access uploaded files.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-2.5 p-3 rounded-lg bg-slate-50">
                  <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <p className="text-xs text-slate-600"><span className="font-semibold">Encrypted</span> in transit and at rest</p>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-lg bg-slate-50">
                  <Eye className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <p className="text-xs text-slate-600"><span className="font-semibold">Private</span> — authorized access only</p>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-lg bg-slate-50">
                  <FileCheck className="w-4 h-4 text-purple-600 flex-shrink-0" />
                  <p className="text-xs text-slate-600"><span className="font-semibold">One-Way</span> — no data is exposed</p>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-8 py-16 text-center">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-3">Upload Complete</h2>
                <p className="text-slate-500 text-lg max-w-md mx-auto mb-2">
                  Your census data has been securely received.
                </p>
                <p className="text-slate-400 text-sm max-w-md mx-auto mb-8">
                  The CityWorth team will process your data privately. No further action is needed on your end.
                </p>
                <div className="inline-flex items-center gap-2 bg-slate-50 rounded-full px-5 py-2 text-sm text-slate-500 border border-slate-100">
                  <ShieldCheck className="w-4 h-4 text-green-500" />
                  Secured & encrypted
                </div>
                <div className="mt-8">
                  <Button
                    onClick={() => { setDone(false); setFile(null); }}
                    data-testid="upload-another-button"
                    variant="outline"
                    className="border-slate-200 text-slate-600"
                  >
                    Upload Another File
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-auto">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Lock className="w-4 h-4" />
            <span>Secure upload portal</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Powered by</span>
            <img src={LOGO_URL} alt="CityWorth" className="h-5 opacity-60" />
          </div>
        </div>
      </footer>
    </div>
  );
}
